import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import Studio from "@/pages/Studio";
import SharePlayer from "@/pages/SharePlayer";
import Landing from "@/pages/Landing";
import Support from "@/pages/Support";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { StudioProvider } from "@/studio/StudioContext";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { SubscriptionProvider } from "@/subscription/SubscriptionContext";
import UpgradeModal from "@/subscription/UpgradeModal";
import AuthModal from "@/auth/AuthModal";
import { Loader2, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";

function StudioGate() {
  const { setShowAuth } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grain flex flex-col items-center justify-center px-6 text-center" data-testid="studio-gate">
      <img src="/logo.png" alt="BeatMXR" className="w-16 h-16 rounded-xl mb-6" />
      <div className="inline-flex items-center gap-2 rounded-full border border-[#00e5ff]/30 bg-[#00e5ff]/10 px-4 py-1.5 mb-5">
        <Lock size={13} className="text-[#00e5ff]" />
        <span className="font-mono-data text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]">Free account required</span>
      </div>
      <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight max-w-md">
        Create your free account to enter the studio
      </h1>
      <p className="mt-4 text-neutral-400 max-w-sm text-sm leading-relaxed">
        It's free to start — sign up to make beats, save your songs across devices, and unlock the AI. No download.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          data-testid="gate-signin-btn"
          onClick={() => setShowAuth(true)}
          className="rounded-full bg-[#00e5ff] text-black px-7 py-3.5 text-sm font-bold hover:-translate-y-0.5 transition-transform"
          style={{ boxShadow: "0 0 28px rgba(0,229,255,0.45)" }}
        >
          Sign In / Create Account
        </button>
        <button
          data-testid="gate-home-btn"
          onClick={() => navigate("/")}
          className="rounded-full border border-white/15 px-6 py-3.5 text-sm font-bold hover:border-white/40 transition-colors"
        >
          Back to Home
        </button>
      </div>
      <AuthModal />
    </div>
  );
}

function StudioApp() {
  const { user, setShowAuth } = useAuth();

  useEffect(() => {
    if (user === false) setShowAuth(true);
  }, [user, setShowAuth]);

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-neutral-400">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  if (!user || !user.id) {
    return <StudioGate />;
  }

  return (
    <StudioProvider>
      <Studio />
      <AuthModal />
    </StudioProvider>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/studio" element={<StudioApp />} />
              <Route path="/support" element={<Support />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route
                path="/s/:shareId"
                element={
                  <StudioProvider>
                    <SharePlayer />
                  </StudioProvider>
                }
              />
            </Routes>
          </BrowserRouter>
          <UpgradeModal />
          <Toaster theme="dark" position="top-center" richColors />
        </SubscriptionProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
